import React, { useEffect, useRef, useState, useCallback } from 'react';
import { marketingAgentService, type MarketingMessage, type AttachmentPayload } from '../services/marketingAgent.service';

// ─── Markdown renderer ──────────────────────────────────────────────────────

function MarkdownText({ text }: { text: string }) {
  const html = text
    // Code blocks
    .replace(/```(\w*)\n?([\s\S]*?)```/g, (_m, _lang, code) =>
      `<pre class="bg-black/40 rounded-lg p-3 overflow-x-auto text-xs my-2 border border-white/10"><code>${escHtml(code.trim())}</code></pre>`)
    // Inline code
    .replace(/`([^`\n]+)`/g, (_m, c) => `<code class="bg-black/30 px-1 py-0.5 rounded text-xs text-[#0CB4E4]">${escHtml(c)}</code>`)
    // Bold
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
    // Headers
    .replace(/^### (.+)$/gm, '<p class="text-sm font-semibold text-white mt-3 mb-1">$1</p>')
    .replace(/^## (.+)$/gm, '<p class="text-base font-bold text-white mt-4 mb-1">$1</p>')
    .replace(/^# (.+)$/gm, '<p class="text-lg font-bold text-white mt-4 mb-2">$1</p>')
    // Bullet lists
    .replace(/^[-•] (.+)$/gm, '<li class="ml-4 list-disc text-gray-300 text-sm">$1</li>')
    // Numbered lists
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal text-gray-300 text-sm">$1</li>')
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="border-white/10 my-3">')
    // Links
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#0CB4E4] underline underline-offset-2 hover:opacity-80">$1</a>')
    // Paragraphs
    .replace(/\n\n/g, '</p><p class="mb-2">')
    .replace(/\n/g, '<br>');

  return (
    <div
      className="text-sm text-gray-200 leading-relaxed [&_li+li]:mt-0.5 [&_p]:mb-2"
      dangerouslySetInnerHTML={{ __html: `<p class="mb-2">${html}</p>` }}
    />
  );
}

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── Attachment preview ──────────────────────────────────────────────────────

interface PendingAttachment extends AttachmentPayload {
  preview?: string; // object URL for images
}

function AttachmentChip({ att, onRemove }: { att: PendingAttachment; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-1.5 bg-white/10 border border-white/15 rounded-lg px-2 py-1">
      {att.type === 'image' && att.preview ? (
        <img src={att.preview} alt={att.name} className="w-6 h-6 rounded object-cover" />
      ) : (
        <span className="text-sm">{att.type === 'pdf' ? '📄' : att.type === 'video' ? '🎬' : '🔗'}</span>
      )}
      <span className="text-xs text-gray-300 max-w-[120px] truncate">{att.name}</span>
      <button onClick={onRemove} className="text-gray-500 hover:text-gray-200 text-xs ml-0.5">✕</button>
    </div>
  );
}

// ─── Message bubble ──────────────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: MarketingMessage }) {
  const isUser = msg.role === 'user';
  const time = new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold mt-1 ${
        isUser ? 'bg-[#0CB4E4]/30 text-[#0CB4E4]' : 'bg-[#252220] border border-white/15 text-gray-400'
      }`}>
        {isUser ? 'Y' : '⬡'}
      </div>

      {/* Bubble */}
      <div className={`max-w-[78%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        {msg.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1">
            {msg.attachments.map((a, i) => (
              <div key={i} className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-1">
                <span className="text-xs">{a.type === 'image' ? '🖼' : a.type === 'pdf' ? '📄' : a.type === 'video' ? '🎬' : '🔗'}</span>
                <span className="text-xs text-gray-400">{a.name}</span>
              </div>
            ))}
          </div>
        )}
        <div className={`rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-[#0CB4E4]/15 border border-[#0CB4E4]/25 text-gray-100 text-sm'
            : 'bg-[#1e1c1a] border border-white/10'
        }`}>
          {isUser
            ? <p className="text-sm text-gray-100 leading-relaxed whitespace-pre-wrap">{msg.text}</p>
            : <MarkdownText text={msg.text} />
          }
        </div>
        <span className="text-[10px] text-gray-600 px-1">{time}</span>
      </div>
    </div>
  );
}

// ─── Typing indicator ────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs bg-[#252220] border border-white/15 text-gray-400">⬡</div>
      <div className="bg-[#1e1c1a] border border-white/10 rounded-2xl px-4 py-3">
        <div className="flex gap-1 items-center h-4">
          {[0, 1, 2].map(i => (
            <span key={i} className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

const ACCEPTED_FILES = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4,video/quicktime,video/webm';
const MAX_VIDEO_MB = 20;

export default function MarketingAgentPage() {
  const [messages, setMessages] = useState<MarketingMessage[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load history on mount
  useEffect(() => {
    marketingAgentService.getHistory(100)
      .then(msgs => setMessages(msgs))
      .catch(() => setError('Failed to load conversation history'))
      .finally(() => setHistoryLoading(false));
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [input]);

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const arr = Array.from(files);
    for (const file of arr) {
      if (file.type.startsWith('video/') && file.size > MAX_VIDEO_MB * 1024 * 1024) {
        setError(`Video must be under ${MAX_VIDEO_MB}MB`);
        continue;
      }
      try {
        const data = await readFileAsBase64(file);
        const type: PendingAttachment['type'] = file.type.startsWith('image/')
          ? 'image' : file.type === 'application/pdf'
          ? 'pdf' : 'video';
        const preview = type === 'image' ? URL.createObjectURL(file) : undefined;
        setPending(p => [...p, { type, name: file.name, mimeType: file.type, data, preview }]);
      } catch {
        setError(`Failed to read ${file.name}`);
      }
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.items)
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean) as File[];
    if (files.length) handleFiles(files);
  }, [handleFiles]);

  const send = async () => {
    const text = input.trim();
    if (!text && pending.length === 0) return;
    if (loading) return;

    setError('');
    setInput('');

    // Optimistic user message
    const optimistic: MarketingMessage = {
      role: 'user',
      text: text || '(attachment)',
      attachments: pending.map(p => ({ type: p.type, name: p.name, mimeType: p.mimeType })),
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    const attachmentsToSend = [...pending];
    setPending([]);
    setLoading(true);

    try {
      const response = await marketingAgentService.sendMessage(text, attachmentsToSend);
      const assistantMsg: MarketingMessage = {
        role: 'assistant',
        text: response,
        attachments: [],
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch {
      setError('Failed to get response — check the server is running');
      setMessages(prev => prev.slice(0, -1)); // remove optimistic
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className="flex flex-col h-screen bg-[#161513]"
      onDrop={handleDrop}
      onDragOver={e => e.preventDefault()}
    >
      {/* Header */}
      <div className="flex-shrink-0 border-b border-white/10 px-5 py-4 flex items-center justify-between bg-[#161513]/95 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-[#0CB4E4]/20 border border-[#0CB4E4]/30 flex items-center justify-center text-base">⬡</div>
          <div>
            <h1 className="text-sm font-semibold text-white">Marketing Agent</h1>
            <p className="text-xs text-gray-500">CR3ATE · shared memory · {messages.length} messages</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
          <span className="text-xs text-gray-500">online</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-5">
          {historyLoading ? (
            <div className="text-center text-gray-500 text-sm animate-pulse py-12">Loading conversation history...</div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 rounded-2xl bg-[#0CB4E4]/15 border border-[#0CB4E4]/25 flex items-center justify-center text-2xl mx-auto mb-4">⬡</div>
              <h2 className="text-white font-semibold mb-2">Marketing Intelligence Agent</h2>
              <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
                Ask about market gaps, competitors, potential customers, partnership opportunities, or marketing channels. Share screenshots, PDFs, videos, or links — I'll analyze them.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {[
                  "What's the gap in the 3D reconstruction market?",
                  "Who are our most promising potential customers?",
                  "What's Agisoft doing that we should respond to?",
                  "Which marketing channels should CR3ATE build presence on?",
                ].map(prompt => (
                  <button
                    key={prompt}
                    onClick={() => setInput(prompt)}
                    className="text-xs text-gray-400 border border-white/10 rounded-xl px-3 py-2 hover:border-[#0CB4E4]/40 hover:text-gray-200 transition-colors text-left"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)
          )}

          {loading && <TypingIndicator />}
          {error && (
            <div className="text-center">
              <span className="text-xs text-red-400 bg-red-900/20 border border-red-500/20 rounded-lg px-3 py-1.5 inline-block">{error}</span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-white/10 bg-[#161513]/95 backdrop-blur-sm px-4 py-4">
        <div className="max-w-3xl mx-auto">
          {/* Pending attachments */}
          {pending.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {pending.map((att, i) => (
                <AttachmentChip
                  key={i}
                  att={att}
                  onRemove={() => setPending(p => p.filter((_, j) => j !== i))}
                />
              ))}
            </div>
          )}

          <div className="flex items-end gap-2 bg-[#1e1c1a] border border-white/15 rounded-2xl px-3 py-2 focus-within:border-[#0CB4E4]/40 transition-colors">
            {/* Attach button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-colors rounded-lg hover:bg-white/5 mb-0.5"
              title="Attach image, PDF, or video"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>

            {/* Text input */}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Ask about market gaps, competitors, customers... or paste a URL / drop a file"
              className="flex-1 bg-transparent text-sm text-gray-100 placeholder-gray-600 resize-none outline-none py-1.5 min-h-[36px] max-h-[160px] leading-relaxed"
              rows={1}
              disabled={loading}
            />

            {/* Send button */}
            <button
              onClick={send}
              disabled={loading || (!input.trim() && pending.length === 0)}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-[#0CB4E4] text-black rounded-lg hover:bg-[#0CB4E4]/80 transition-colors disabled:opacity-30 disabled:cursor-not-allowed mb-0.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <p className="text-[10px] text-gray-600 text-center mt-2">
            Shift+Enter for new line · Drag & drop or paste images · URLs auto-fetched
          </p>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILES}
        multiple
        className="hidden"
        onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}
